import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaDownload } from 'react-icons/fa';
import { useTheme } from '../../context/ThemeContext';
import { encodeQr, toSvg, ERROR_LEVELS, capacityBytes, toUtf8Bytes } from '../../lib/qrcode';
import { buttonClass, fieldClass, labelClass, mutedClass, panelClass, tabClass } from '../uiStyles';

const PNG_MODULE_SIZE = 10;
const QUIET_ZONE = 4;

const download = (href, filename) => {
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const QrGenerator = () => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [text, setText] = useState('https://about.doon.uz');
  const [level, setLevel] = useState('M');

  const { qr, error } = useMemo(() => {
    try {
      return { qr: encodeQr(text, level), error: null };
    } catch (caught) {
      return { qr: null, error: caught.message };
    }
  }, [text, level]);

  // The symbol is always drawn black-on-white: inverting it for dark mode would
  // stop a good share of scanners from reading it.
  const svg = qr ? toSvg(qr, { scale: 8, margin: QUIET_ZONE }) : '';

  const downloadPng = () => {
    if (!qr) return;
    const dimension = (qr.size + QUIET_ZONE * 2) * PNG_MODULE_SIZE;
    const canvas = document.createElement('canvas');
    canvas.width = dimension;
    canvas.height = dimension;
    const context = canvas.getContext('2d');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, dimension, dimension);
    context.fillStyle = '#000000';
    qr.modules.forEach((row, y) => row.forEach((dark, x) => {
      if (dark) {
        context.fillRect(
          (x + QUIET_ZONE) * PNG_MODULE_SIZE,
          (y + QUIET_ZONE) * PNG_MODULE_SIZE,
          PNG_MODULE_SIZE,
          PNG_MODULE_SIZE,
        );
      }
    }));
    download(canvas.toDataURL('image/png'), 'qr-kod.png');
  };

  const downloadSvg = () => {
    if (!svg) return;
    download(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`, 'qr-kod.svg');
  };

  const usedBytes = toUtf8Bytes(text).length;

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="space-y-4">
        <div>
          <label className={labelClass(isDarkMode)} htmlFor="qr-text">
            {t('useful.qr.text')}
          </label>
          <textarea
            id="qr-text"
            value={text}
            onChange={(event) => setText(event.target.value)}
            rows={5}
            placeholder={t('useful.qr.placeholder')}
            className={`${fieldClass(isDarkMode)} resize-y font-mono text-sm`}
          />
          <p className={`text-xs mt-1 ${mutedClass(isDarkMode)}`}>
            {usedBytes} / {capacityBytes(level)} {t('useful.qr.bytes')}
          </p>
        </div>

        <div>
          <span className={labelClass(isDarkMode)}>{t('useful.qr.level')}</span>
          <div className="flex flex-wrap gap-2">
            {ERROR_LEVELS.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setLevel(option)}
                className={tabClass(isDarkMode, level === option)}
              >
                {option} · {t(`useful.qr.levels.${option}`)}
              </button>
            ))}
          </div>
          <p className={`text-xs mt-2 ${mutedClass(isDarkMode)}`}>{t('useful.qr.levelHint')}</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={downloadPng}
            disabled={!qr}
            className={`${buttonClass(isDarkMode, 'primary')} inline-flex items-center gap-2`}
          >
            <FaDownload /> PNG
          </button>
          <button
            type="button"
            onClick={downloadSvg}
            disabled={!qr}
            className={`${buttonClass(isDarkMode)} inline-flex items-center gap-2`}
          >
            <FaDownload /> SVG
          </button>
        </div>
      </div>

      <div className={`${panelClass(isDarkMode)} flex flex-col items-center justify-center gap-3`}>
        {qr ? (
          <>
            <div
              className="w-full max-w-[280px] rounded-lg overflow-hidden bg-white p-2"
              /* The SVG is built from our own encoder output — no user markup reaches it. */
              dangerouslySetInnerHTML={{ __html: svg }}
            />
            <p className={`text-xs ${mutedClass(isDarkMode)}`}>
              {t('useful.qr.info', { version: qr.version, size: qr.size, level: qr.level })}
            </p>
          </>
        ) : (
          <p className={`text-sm text-center ${mutedClass(isDarkMode)}`}>
            {error === 'qr-too-long' ? t('useful.qr.tooLong') : t('useful.qr.empty')}
          </p>
        )}
      </div>
    </div>
  );
};

export default QrGenerator;
